// Types + validation for the two-step "Add new ingredient" flow.
// Structure mirrors Team/AddNutritionist/types.ts so the flow stays
// familiar and both features can share the same step/indicator UI.

import type { IngredientDetail } from "../types";

export const createId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/* ── Step 1: Details & nutrition ─────────────────────────────── */

// Micro-nutrient field keys. Kept as a list so the form and validation can
// iterate without repeating the names. Order here drives the render order.
export const MICRO_NUTRIENT_FIELDS = [
  "vitaminA",
  "vitaminD",
  "vitaminE",
  "vitaminK",
  "vitaminC",
  "thiamine",
  "riboflavin",
  "niacin",
  "pantothenicAcid",
  "vitaminB6",
  "biotin",
  "folate",
  "vitaminB12",
  "choline",
  "calcium",
  "phosphorus",
  "magnesium",
  "sodium",
  "potassium",
  "chloride",
  "iron",
  "zinc",
  "copper",
  "manganese",
  "selenium",
  "iodine",
  "chromium",
  "molybdenum",
  "fluoride",
] as const;

export type MicroNutrientKey = (typeof MICRO_NUTRIENT_FIELDS)[number];

export type MicroNutrients = Record<MicroNutrientKey, string>;

export const initialMicroNutrients: MicroNutrients = MICRO_NUTRIENT_FIELDS.reduce(
  (acc, key) => {
    acc[key] = "";
    return acc;
  },
  {} as MicroNutrients,
);

// Frontend camelCase → backend snake_case key mapping. Used when serialising
// the micro-nutrient values into the CREATE_INGREDIENT / UPDATE_INGREDIENT
// multipart form (the backend expects fields like `vitamin_a`, `thiamine_b1`).
export const MICRO_NUTRIENT_BACKEND_KEY: Record<MicroNutrientKey, string> = {
  vitaminA: "vitamin_a",
  vitaminD: "vitamin_d",
  vitaminE: "vitamin_e",
  vitaminK: "vitamin_k",
  vitaminC: "vitamin_c",
  thiamine: "thiamine_b1",
  riboflavin: "riboflavin_b2",
  niacin: "niacin_b3",
  pantothenicAcid: "pantothenic_acid_b5",
  vitaminB6: "vitamin_b6",
  biotin: "biotin_b7",
  folate: "folate_b9",
  vitaminB12: "vitamin_b12",
  choline: "choline",
  calcium: "calcium",
  phosphorus: "phosphorus",
  magnesium: "magnesium",
  sodium: "sodium",
  potassium: "potassium",
  chloride: "chloride",
  iron: "iron",
  zinc: "zinc",
  copper: "copper",
  manganese: "manganese",
  selenium: "selenium",
  iodine: "iodine",
  chromium: "chromium",
  molybdenum: "molybdenum",
  fluoride: "fluoride",
};

export interface DetailsNutritionData {
  photo: File | null;
  photoPreview: string | null;
  name: string;
  unit: string;
  baseQty: string;
  category: string;
  type: string;
  calories: string;
  adjProtein: string;
  grossProtein: string;
  carbs: string;
  netCarbs: string;
  fat: string;
  gl: string;
  fiber: string;
  micro: MicroNutrients;
  allergens: string[];
  tags: string[];
}

export interface DetailsNutritionErrors {
  name?: string;
  unit?: string;
  baseQty?: string;
  category?: string;
  type?: string;
  calories?: string;
  adjProtein?: string;
  grossProtein?: string;
  carbs?: string;
  netCarbs?: string;
  fat?: string;
  gl?: string;
  fiber?: string;
  allergens?: string;
  tags?: string;
}

export const initialDetailsNutrition: DetailsNutritionData = {
  photo: null,
  photoPreview: null,
  name: "",
  unit: "",
  baseQty: "",
  category: "",
  type: "",
  calories: "",
  adjProtein: "",
  grossProtein: "",
  carbs: "",
  netCarbs: "",
  fat: "",
  gl: "",
  fiber: "",
  micro: { ...initialMicroNutrients },
  allergens: [],
  tags: [],
};

const isNonEmpty = (v: string) => v.trim().length > 0;
const isPositiveNumber = (v: string) => {
  if (!isNonEmpty(v)) return false;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0;
};

export const validateDetailsNutrition = (
  data: DetailsNutritionData,
): DetailsNutritionErrors => {
  const errors: DetailsNutritionErrors = {};
  if (!isNonEmpty(data.name)) errors.name = "Ingredient name is required";
  if (!isNonEmpty(data.unit)) errors.unit = "Unit is required";
  if (!isPositiveNumber(data.baseQty)) errors.baseQty = "Enter a valid quantity";
  if (!isNonEmpty(data.category)) errors.category = "Category is required";
  if (!isNonEmpty(data.type)) errors.type = "Type is required";
  if (!isPositiveNumber(data.calories)) errors.calories = "Enter a valid number";
  if (!isPositiveNumber(data.adjProtein)) errors.adjProtein = "Enter a valid number";
  if (!isPositiveNumber(data.grossProtein))
    errors.grossProtein = "Enter a valid number";
  // Carbs is optional — only validate when the user has typed something.
  if (isNonEmpty(data.carbs) && !isPositiveNumber(data.carbs))
    errors.carbs = "Enter a valid number";
  if (!isPositiveNumber(data.netCarbs)) errors.netCarbs = "Enter a valid number";
  if (!isPositiveNumber(data.fat)) errors.fat = "Enter a valid number";
  if (!isPositiveNumber(data.gl)) errors.gl = "Enter a valid number";
  return errors;
};

/* ── Step 2: Unit conversion ────────────────────────────────── */

export interface ConversionRow {
  id: string;
  /* gm/oz rows are locked (fixed base conversions); user-added rows
     let the user pick a unit and set the grams equivalent. */
  locked: boolean;
  unit: string;
  grams: string;
}

export interface UnitConversionData {
  rows: ConversionRow[];
}

export interface UnitConversionErrors {
  rows?: Record<string, { unit?: string; grams?: string }>;
}

// Locked base rows for Step 2 are derived from Step 1's chosen unit so the
// user sees the correct measurement family (gm/oz for solids, ml/fl oz for
// liquids) without having to type them in again.
export const buildBaseConversionRows = (unit: string): ConversionRow[] => {
  const u = unit.trim().toLowerCase();
  if (u === "ml") {
    return [
      { id: createId(), locked: true, unit: "ml", grams: "1" },
      { id: createId(), locked: true, unit: "fl oz", grams: "29.5735" },
    ];
  }
  return [
    { id: createId(), locked: true, unit: "gm", grams: "1" },
    { id: createId(), locked: true, unit: "oz", grams: "28.3495" },
  ];
};

export const initialUnitConversion: UnitConversionData = {
  rows: buildBaseConversionRows(""),
};

export const validateUnitConversion = (
  data: UnitConversionData,
): UnitConversionErrors => {
  const rowErrors: Record<string, { unit?: string; grams?: string }> = {};
  data.rows.forEach((row) => {
    if (row.locked) return;
    const rErr: { unit?: string; grams?: string } = {};
    if (!isNonEmpty(row.unit)) rErr.unit = "Select a unit";
    if (!isPositiveNumber(row.grams)) rErr.grams = "Enter grams";
    if (Object.keys(rErr).length > 0) rowErrors[row.id] = rErr;
  });
  if (Object.keys(rowErrors).length === 0) return {};
  return { rows: rowErrors };
};

/* ── Combined form data ─────────────────────────────────────── */

export interface AddIngredientFormData {
  details: DetailsNutritionData;
  conversion: UnitConversionData;
}

/* ── Edit-mode adapter ──────────────────────────────────────── */

// Map "veg"/"nonveg"/"none" (list-side) → the Type dropdown value.
const dietTypeToType = (t: IngredientDetail["dietType"]): string => {
  if (t === "veg") return "veg";
  if (t === "nonveg") return "nonveg";
  return "";
};

// Base quantity in the detail record is stored like "100g" — strip the unit
// suffix to get back the numeric qty the form field expects. Normalise a
// few common short forms so the parsed unit still matches a dropdown option
// (form uses "gm", but detail records may have been saved as "g").
const UNIT_ALIASES: Record<string, string> = {
  g: "gm",
  gms: "gm",
  gram: "gm",
  grams: "gm",
  milliliter: "ml",
  milliliters: "ml",
};

const splitBaseQuantity = (
  raw: string,
): { qty: string; unit: string } => {
  const match = raw.match(/^\s*([\d.]+)\s*([a-zA-Z]*)\s*$/);
  if (!match) return { qty: raw, unit: "" };
  const rawUnit = (match[2] ?? "").toLowerCase();
  return { qty: match[1] ?? "", unit: UNIT_ALIASES[rawUnit] ?? rawUnit };
};

// Allergens are stored as display strings ("Gluten"); the dropdown works with
// lowercase values ("gluten"). Convert both sides consistently.
const allergenToValue = (label: string) => label.trim().toLowerCase();

// Numeric string coming from the detail endpoint often looks like "150.00" or
// "0.05" — strip trailing zeros so the input doesn't show padded decimals.
const trimMicroValue = (raw: unknown): string => {
  if (raw === undefined || raw === null || raw === "") return "";
  const n = Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  return String(parseFloat(n.toFixed(6)));
};

export const buildFormFromIngredient = (
  ingredient: IngredientDetail,
): AddIngredientFormData => {
  const { qty, unit } = splitBaseQuantity(ingredient.baseQuantity);
  // Populate the micro form state from the detail record — values are keyed
  // by backend snake_case names, so translate through MICRO_NUTRIENT_BACKEND_KEY.
  const micro: MicroNutrients = { ...initialMicroNutrients };
  if (ingredient.micronutrients) {
    MICRO_NUTRIENT_FIELDS.forEach((key) => {
      const backendKey = MICRO_NUTRIENT_BACKEND_KEY[key];
      micro[key] = trimMicroValue(ingredient.micronutrients?.[backendKey]);
    });
  }
  return {
    details: {
      photo: null,
      photoPreview: ingredient.imageUrl,
      name: ingredient.name,
      unit,
      baseQty: qty,
      category: ingredient.category ?? "",
      type: dietTypeToType(ingredient.dietType),
      calories: String(ingredient.calories),
      adjProtein: String(ingredient.adjProtein),
      grossProtein: String(ingredient.grossProtein ?? ""),
      carbs: String(ingredient.carbs ?? ""),
      netCarbs: String(ingredient.netCarbs),
      fat: String(ingredient.fats),
      gl: String(ingredient.gl),
      fiber: ingredient.fiber !== undefined ? String(ingredient.fiber) : "",
      micro,
      allergens: ingredient.allergens.map(allergenToValue),
      tags: ingredient.tags ?? [],
    },
    conversion: {
      // Base rows (gm/oz or ml/fl oz) can appear in any position in the API
      // response, so identify them by unit type instead of index. Locked
      // rows come first, then custom rows, so the layout matches the add
      // flow.
      rows: (() => {
        const baseRows = buildBaseConversionRows(unit);
        const baseUnitSet = new Set(
          baseRows.map((r) => r.unit.toLowerCase()),
        );
        const customRows = ingredient.unitConversions
          .filter((row) => !baseUnitSet.has(row.unit.trim().toLowerCase()))
          .map((row) => {
            // Grams comes formatted as "1 g" / "150 g" — pull just the number.
            const gramsMatch = row.grams.match(/[\d.]+/);
            const grams = gramsMatch ? gramsMatch[0] : row.grams;
            return {
              id: createId(),
              locked: false,
              // Both the dropdown options and the submit payload key off the
              // API's raw `unit_type` string, so pass it through unchanged.
              unit: row.unit,
              grams,
            };
          });
        return [...baseRows, ...customRows];
      })(),
    },
  };
};
